import * as PIXI from 'pixi.js';

import { TypedParentMixin } from '../../../parentMixin';
import { TypedUpdateFlowMixin } from '../../../updateFlowMixin';
import { TypedSerializableMixin } from '../../../serializableMixin';
import {
    BooleanAttribute,
    getAttributesToSerialize,
    NumberAttribute,
    StringAttribute,
    StringOrNumberAttribute,
} from '../../../attributes';
import { Config } from '../../../../video-data/config';
import type { TextDynamicAnimationRendererSettings } from '../renderer/TextDynamicAnimationBase';
import type { DynamicLevels } from '../../../../enums';
import { CreationSource, DynamicLevel, DynamicInferredDimensions } from '../../../../enums';
import type { RemoveAnchoredPoints } from '../../../../libs/Curve';
import type { TextAssetStyle } from '../TextAssetStyle';
import type { TextAssetDynamicGraphic } from './TextAssetDynamicGraphic';

const MIN_DURATION = 0;
const MAX_DURATION = 10000;

/**
 * TextAssetDynamicAnimation is the Luminary equivalent of TextDynamicAnimation
 * and provides a bridge between Luminary
 * and the CanvasText library.
 */
export class TextAssetDynamicAnimation extends TypedUpdateFlowMixin(
    TypedParentMixin(TypedSerializableMixin(PIXI.utils.EventEmitter)),
) {
    override _classType = 'CanvasTextAssetDynamicAnimation';
    override _name = 'CanvasTextAssetDynamicAnimation';

    @BooleanAttribute()
    accessor enabled = true;
    @StringAttribute({ choices: Object.values(CreationSource) })
    accessor creationSource: CreationSource = CreationSource.SceneDesign;
    @StringAttribute()
    accessor property: string = 'alpha';
    @StringAttribute({ choices: Object.values(DynamicLevel) })
    accessor level: DynamicLevels = DynamicLevel.object;
    @NumberAttribute({ min: MIN_DURATION, max: MAX_DURATION })
    accessor duration: number = MIN_DURATION;
    @StringAttribute({ choices: Object.values(Config.ALL_INTERPOLATION_PRESETS) })
    accessor interpolation: (typeof Config.ALL_INTERPOLATION_PRESETS)[number] =
        Config.EASE_IN_INTERPOLATION_PRESET;
    @BooleanAttribute()
    accessor zigZagPerLine = false;
    @StringAttribute({ choices: Config.ALL_ZIG_ZAG_START_OPTIONS })
    accessor zigZagPerLineStart = Config.ZIG_ZAG_START_LEFT;
    @BooleanAttribute()
    accessor shouldOffsetPropertyValue = false;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredDimensions) })
    accessor startValue: number | DynamicInferredDimensions = 0;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredDimensions) })
    accessor endValue: number | DynamicInferredDimensions = 0;
    @NumberAttribute()
    accessor startMulti: number = 1;
    @NumberAttribute()
    accessor endMulti: number = 1;

    // Only for animations driven by the animation manager
    @NumberAttribute()
    accessor staggerDelay: number = 0;
    @NumberAttribute()
    accessor delay: number = 0;
    @BooleanAttribute()
    accessor mirrorStaggerDelay = false;
    @StringAttribute({ choices: Config.ANIMATION_CURVE_ANCHORS, serialize: false })
    accessor anchorsToRemove: RemoveAnchoredPoints = Config.NONE_ANIMATION_CURVE_ANCHOR;

    getCanvasRendererSettings(): TextDynamicAnimationRendererSettings {
        const parent = this.parent as TextAssetStyle | TextAssetDynamicGraphic;
        let startValue = this.startValue;
        let endValue = this.endValue;

        // we need to remap geometric or spatial properties from the reference resolution to the output resolution to
        // ensure that the animation is consistent across different resolutions
        if (parent && parent.animatableGeometricProperties.includes(this.property)) {
            if (typeof startValue === 'number') {
                startValue *= this.referenceToOutputMultiplier;
            }
            if (typeof endValue === 'number') {
                endValue *= this.referenceToOutputMultiplier;
            }
        }

        return {
            enabled: this.enabled,
            property: this.property,
            level: this.level,
            staggerDelay: this.staggerDelay,
            delay: this.delay,
            duration: this.duration,
            startValue,
            endValue,
            startMulti: this.startMulti,
            endMulti: this.endMulti,
            interpolation: this.interpolation,
            zigZagPerLine: this.zigZagPerLine,
            zigZagPerLineStart: this.zigZagPerLineStart,
            shouldOffsetPropertyValue: this.shouldOffsetPropertyValue,
            mirrorStaggerDelay: this.mirrorStaggerDelay,
            anchorsToRemove: this.anchorsToRemove,
        };
    }

    /**
     * The scaling multiplier to take a reference dimension to the output dimension.
     * e.g. myOutputCoord =  myReferenceCoord * this.referenceToOutputMultiplier
     */
    get referenceToOutputMultiplier() {
        return this.root.outputResolution.width / this.root.referenceResolution.width;
    }

    override get propertiesToSerialize() {
        return getAttributesToSerialize(this);
    }

    override get readonlyProperties() {
        return [];
    }
}
